import { Navigate, Routes, Route, useLocation } from 'react-router-dom'
import Layout from '@/components/layout/Layout'
import ErrorBoundary from '@/components/ErrorBoundary'
import Dashboard from '@/pages/Dashboard'
import Members from '@/pages/Members'
import Profile from '@/pages/Profile'
import Matches from '@/pages/Matches'
import Events from '@/pages/Events'
import Calendar from '@/pages/Calendar'
import SettingsPage from '@/pages/Settings'
import Login from '@/pages/Login'
import Applications from '@/pages/Applications'
import Supporters from '@/pages/Supporters'
import Briefing from '@/pages/Briefing'
import JoinApp from '@/JoinApp'
import HomeApp from '@/HomeApp'
import Strategy from '@/pages/Strategy'
import Activity from '@/pages/Activity'
import StrategyMap from '@/pages/StrategyMap'
import StrategicPointsMap from '@/pages/StrategicPointsMap'
import { AuthProvider, useAuth, isAdmin, isOperator } from '@/contexts/AuthContext'
import { LanguageProvider } from '@/contexts/LanguageContext'
import { isJoinSite, isHomeSite } from '@/lib/site'

/** The login route, carrying where the visitor was actually headed.
 *
 * A DM link opens in whatever browser Discord hands it to, which often has no
 * session yet. Without this the destination is dropped at the door and the
 * login lands on the dashboard front page — the match the link named is then
 * one the reader has to go find.
 */
function loginPath(pathname: string, search: string) {
  const next = pathname + search
  return next === '/' ? '/login' : `/login?next=${encodeURIComponent(next)}`
}

function ProtectedRoute({ children }: { children: React.ReactNode }) {
  const { user, loading } = useAuth()
  const loc = useLocation()
  if (loading) return <div className="flex h-screen w-full items-center justify-center bg-background"><div className="h-6 w-6 animate-spin rounded-full border-2 border-muted-foreground border-t-transparent" /></div>
  if (!user) return <Navigate to={loginPath(loc.pathname, loc.search)} replace />
  return <>{children}</>
}

function AdminRoute({ children }: { children: React.ReactNode }) {
  const { user, loading } = useAuth()
  const loc = useLocation()
  if (loading) return <div className="flex h-screen w-full items-center justify-center bg-background"><div className="h-6 w-6 animate-spin rounded-full border-2 border-muted-foreground border-t-transparent" /></div>
  if (!user) return <Navigate to={loginPath(loc.pathname, loc.search)} replace />
  if (!isAdmin(user)) return <Navigate to="/" replace />
  return <>{children}</>
}

function OperatorRoute({ children }: { children: React.ReactNode }) {
  const { user, loading } = useAuth()
  const loc = useLocation()
  if (loading) return <div className="flex h-screen w-full items-center justify-center bg-background"><div className="h-6 w-6 animate-spin rounded-full border-2 border-muted-foreground border-t-transparent" /></div>
  if (!user) return <Navigate to={loginPath(loc.pathname, loc.search)} replace />
  if (!isOperator(user)) return <Navigate to="/" replace />
  return <>{children}</>
}

function LoginRoute() {
  const { user, loading } = useAuth()
  const loc = useLocation()
  if (loading) return <div className="flex h-screen w-full items-center justify-center bg-background"><div className="h-6 w-6 animate-spin rounded-full border-2 border-muted-foreground border-t-transparent" /></div>
  // Already signed in: honour the destination anyway. The same link lands here
  // whenever a guard bounced a still-loading session, and dropping `next` at
  // that moment would silently swallow the deep link.
  if (user) {
    const next = new URLSearchParams(loc.search).get('next')
    return <Navigate to={next && next.startsWith('/') && !next.startsWith('//') ? next : '/'} replace />
  }
  return <Login />
}

function AppRoutes() {
  return (
    <Routes>
      <Route path="/login" element={<LoginRoute />} />
      <Route
        element={
          <ProtectedRoute>
            <Layout />
          </ProtectedRoute>
        }
      >
        <Route path="/" element={<Dashboard />} />
        <Route path="/members" element={<Members />} />
        <Route path="/profile" element={<Profile />} />
        <Route path="/matches" element={<Matches />} />
        <Route path="/events" element={<Events />} />
        <Route path="/calendar" element={<Calendar />} />
        <Route path="/activity" element={<Activity />} />
        {/* Strateji sekmesi üç bölümdür ve her bölümün kendi adresi var
            (`/strategy/mac`, `/strategy/noktalar`, `/strategy/defter`); çıplak
            `/strategy` ilk bölüme düşer. Editör bölümün altında açılır, çünkü
            aynı harita iki bölümde birden strateji taşıyabilir. */}
        <Route
          path="/strategy"
          element={
            <ProtectedRoute>
              <ErrorBoundary>
                <Strategy />
              </ErrorBoundary>
            </ProtectedRoute>
          }
        />
        <Route
          path="/strategy/:section"
          element={
            <ProtectedRoute>
              <ErrorBoundary>
                <Strategy />
              </ErrorBoundary>
            </ProtectedRoute>
          }
        />
        <Route
          path="/strategy/:section/:mapName"
          element={
            <ProtectedRoute>
              <StrategyMap />
            </ProtectedRoute>
          }
        />
        <Route
          path="/strategic-points/:mapName"
          element={
            <ProtectedRoute>
              <ErrorBoundary>
                <StrategicPointsMap />
              </ErrorBoundary>
            </ProtectedRoute>
          }
        />
        <Route
          path="/applications"
          element={
            <OperatorRoute>
              <Applications />
            </OperatorRoute>
          }
        />
        <Route
          path="/supporters"
          element={
            <OperatorRoute>
              <Supporters />
            </OperatorRoute>
          }
        />
        <Route
          path="/briefing"
          element={
            <OperatorRoute>
              <Briefing />
            </OperatorRoute>
          }
        />
        <Route
          path="/settings"
          element={
            <AdminRoute>
              <SettingsPage />
            </AdminRoute>
          }
        />
      </Route>
    </Routes>
  )
}

export default function App() {
  // turksletloose.com shares this bundle but not the dashboard shell: it is the
  // public front door, so no AuthProvider and no sidebar.
  if (isHomeSite) {
    return (
      <ErrorBoundary>
        <LanguageProvider>
          <HomeApp />
        </LanguageProvider>
      </ErrorBoundary>
    )
  }

  // join.turksletloose.com shares this bundle but not the dashboard shell:
  // its visitors are applicants, so there is no AuthProvider and no sidebar.
  if (isJoinSite) {
    return (
      <ErrorBoundary>
        <LanguageProvider>
          <JoinApp />
        </LanguageProvider>
      </ErrorBoundary>
    )
  }

  return (
    <ErrorBoundary>
      <LanguageProvider>
        <AuthProvider>
          <AppRoutes />
        </AuthProvider>
      </LanguageProvider>
    </ErrorBoundary>
  )
}
